import type { Post } from '@/services/posts';
import { fromZonedTime } from 'date-fns-tz';

export type RunwayEntry = Pick<Post, 'id' | 'status'> &
  Partial<Pick<Post, 'variationId' | 'displayStatus' | 'source'>>;
export type RunwayCalendarData = Record<string, RunwayEntry[]>;

export interface RunwayDay {
  key: string;
  published: number;
  scheduled: number;
  mapped: number;
  publishing: number;
  issues: number;
}

export interface PlanningRunwaySummary {
  days: RunwayDay[];
  published: number;
  scheduled: number;
  mapped: number;
  publishing: number;
  issues: number;
  committed: number;
  remaining: number;
  covered: boolean;
}

const DAY_MS = 86_400_000;
const issueStatuses = new Set(['FAILED', 'MISSED', 'PARTIAL']);

/** Brand-local date keys are advanced as UTC calendar dates, so DST never shifts a slot. */
export function nextSevenDayKeys(todayKey: string): string[] {
  const start = Date.parse(`${todayKey}T00:00:00Z`);
  if (!Number.isFinite(start)) return [];
  return Array.from({ length: 7 }, (_, offset) => new Date(start + offset * DAY_MS).toISOString().slice(0, 10));
}

/** Choose an instant that displays as the selected date in BOTH calendars. */
export function sharedDayAnchor(dayKey: string, brandTimezone: string, viewerTimezone: string): Date | null {
  const start = Date.parse(`${dayKey}T00:00:00Z`);
  if (!Number.isFinite(start)) return null;
  const nextKey = new Date(start + DAY_MS).toISOString().slice(0, 10);
  try {
    const brandStart = fromZonedTime(`${dayKey}T00:00:00`, brandTimezone).getTime();
    const brandEnd = fromZonedTime(`${nextKey}T00:00:00`, brandTimezone).getTime();
    const viewerStart = fromZonedTime(`${dayKey}T00:00:00`, viewerTimezone).getTime();
    const viewerEnd = fromZonedTime(`${nextKey}T00:00:00`, viewerTimezone).getTime();
    const overlapStart = Math.max(brandStart, viewerStart);
    const overlapEnd = Math.min(brandEnd, viewerEnd);
    return Number.isFinite(overlapStart) && Number.isFinite(overlapEnd) && overlapEnd > overlapStart
      ? new Date(Math.floor((overlapStart + overlapEnd) / 2))
      : null;
  } catch {
    return null;
  }
}

function stateOf(post: RunwayEntry): keyof Omit<RunwayDay, 'key'> | null {
  if (post.source === 'blog' || post.status === 'CANCELLED') return null;
  const status = post.displayStatus ?? post.status;
  if (issueStatuses.has(status)) return 'issues';
  if (status === 'PUBLISHED') return 'published';
  if (status === 'SCHEDULED') return 'scheduled';
  if (status === 'PUBLISHING') return 'publishing';
  // A dated mapping is a plan even while its content awaits review or approval.
  if (post.source === 'mapped' || status === 'MAPPED') return 'mapped';
  return null;
}

const priority: Record<keyof Omit<RunwayDay, 'key'>, number> = {
  issues: 5,
  publishing: 4,
  published: 3,
  scheduled: 2,
  mapped: 1,
};

/** One content item per brand-local day, even when it appears on several calendars. */
export function summarizePlanningRunway(
  calendars: RunwayCalendarData[],
  todayKey: string,
  weeklyTarget: number,
): PlanningRunwaySummary {
  const days = nextSevenDayKeys(todayKey).map((key): RunwayDay => {
    const items = new Map<string, keyof Omit<RunwayDay, 'key'>>();
    for (const calendar of calendars) {
      for (const post of calendar[key] ?? []) {
        const state = stateOf(post);
        if (!state) continue;
        const itemKey = `${post.id}:${post.variationId ?? 0}`;
        const prior = items.get(itemKey);
        if (!prior || priority[state] > priority[prior]) items.set(itemKey, state);
      }
    }
    const day: RunwayDay = { key, published: 0, scheduled: 0, mapped: 0, publishing: 0, issues: 0 };
    for (const state of items.values()) day[state] += 1;
    return day;
  });
  const totals = days.reduce(
    (sum, day) => ({
      published: sum.published + day.published,
      scheduled: sum.scheduled + day.scheduled,
      mapped: sum.mapped + day.mapped,
      publishing: sum.publishing + day.publishing,
      issues: sum.issues + day.issues,
    }),
    { published: 0, scheduled: 0, mapped: 0, publishing: 0, issues: 0 },
  );
  const committed = totals.published + totals.scheduled;
  const remaining = Math.max(0, weeklyTarget - committed);
  return { days, ...totals, committed, remaining, covered: weeklyTarget > 0 && remaining === 0 && totals.issues === 0 };
}
